import { ExportOptions } from '../../../components/modals/export-records-modal/enums/export-options-enum';
import { baseColumns } from '../../../components/shipping-grid/columns';
import { emptyArray } from 'ui-components/default-props/empty';
import { TYPE_STRING, TYPE_COLLECTION, TYPE_DATE_COMPARISON, TYPE_ENUM } from '../../../filters/options/input-types';
import { ColumnOrderByParamService } from '../../../services/column-orderby-param-service';
import { DateService } from '../../../services/date-service';
import { mapCriterionToApiParam } from '../../../services/filtering/api-param-map-service';
import { BatchDetailsHelper } from '../../../store/batches/details/batch-details-helper';
import { GeneralExportHelper } from '../general-export-helper';
import { messages } from '../messages';
import { uiColumnToExportColumnMap } from './ui-column-to-export-column-map';
import { getSelectedViewFromPathName } from '../../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';
import GlobalSelectors from '../../global-selectors';

const DEFAULT_EXPORT_FORMAT = 'CSV';

export const filterTypeToApiFilterMap = {
  [TYPE_STRING]: {
    createFilter: ({ value, apiFilter }) => {
      const trimValue = value.filter((v) => v.trim());
      if (trimValue.length > 0) {
        return { [apiFilter]: value[0] };
      }
    },
  },
  [TYPE_COLLECTION]: {
    createFilter: ({ value, apiFilter }) => {
      if (value.length > 0) {
        return { [apiFilter]: value[0] };
      }
    },
  },
  [TYPE_DATE_COMPARISON]: {
    createFilter: ({ value, apiFilter }) => {
      const { from, to } = value;
      const fromFilter =
        from != null
          ? {
              [`${apiFilter}Gte`]: DateService.getFormattedDate(from),
            }
          : {};
      const toFilter =
        to != null
          ? {
              [`${apiFilter}Lt`]: DateService.getFormattedDate(to),
            }
          : {};
      return { ...fromFilter, ...toFilter };
    },
  },
  [TYPE_ENUM]: {
    createFilter: ({ value, apiFilter }) => ({
      [apiFilter]: value,
    }),
  },
};

class ShipmentExportHelperClass {
  getExportColumnNameFromUiColumnName(uiColumnName) {
    return uiColumnToExportColumnMap[uiColumnName];
  }

  getExportColumnsForLineItems(intl) {
    return [
      {
        columnId: 'ItemName',
        name: intl.formatMessage(messages.recordExportLineItemName),
      },
      {
        columnId: 'ItemQuantity',
        name: intl.formatMessage(messages.recordExportLineItemQuantity),
      },
      {
        columnId: 'ItemSku',
        name: intl.formatMessage(messages.recordExportLineItemSku),
      },
    ];
  }

  getRequestColumnConfiguration(columnConfiguration, intl) {
    return columnConfiguration.map((column) => {
      const base = baseColumns.find((baseColumn) => baseColumn.id === column.id) || {};
      const messageDescriptor = base.getMessageDescriptor && base.getMessageDescriptor();
      let name = base.getName && base.getName();

      if (intl && messageDescriptor) {
        name = intl.formatMessage(messageDescriptor);
      }
      return {
        columnId: this.getExportColumnNameFromUiColumnName(column.id),
        name,
      };
    });
  }

  getExportRequestParameters(state, viewRecordsOrSelectedRecords, primaryRecordsOrLineItems, intl, _, filters = {}, history) {
    const statusViewId = getSelectedViewFromPathName(history?.location?.pathname)?.id;
    const activeFilterView = GlobalSelectors.selectShipmentsFilteringActiveFilterView(state);
    const activeFilterViewId = activeFilterView.filterViewId;

    const orderByColumn = GlobalSelectors.selectShipmentsGridCurrentOrderByColumn(state, {
      statusViewId,
      activeFilterViewId,
    });
    const orderByParam = ColumnOrderByParamService.getShippingGridOrderByApiParamOrDefault(orderByColumn, true);

    const columnConfiguration = GlobalSelectors.selectShipmentsGridCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
    });
    const exportColumnsWithoutLineItems = this.getRequestColumnConfiguration(columnConfiguration, intl);
    const fulfillmentIds =
      viewRecordsOrSelectedRecords === ExportOptions.EXPORT_ALL_RECORDS_IN_VIEW
        ? emptyArray
        : GlobalSelectors.selectSelectedShipmentsSelectedShipmentIds(state) || emptyArray;
    const shouldExportLineItems = primaryRecordsOrLineItems === ExportOptions.EXPORT_PRIMARY_RECORDS_AND_LINE_ITEMS;
    const exportColumns = shouldExportLineItems
      ? [...exportColumnsWithoutLineItems, ...this.getExportColumnsForLineItems(intl)]
      : exportColumnsWithoutLineItems;
    const searchPerfromed = GlobalSelectors.selectShipmentsNavSearchPerformed(state);
    const { searchTerm } = GlobalSelectors.selectShipmentsNavSearchData(state);

    const requestBody = {
      fileType: DEFAULT_EXPORT_FORMAT,
      exportColumns: exportColumns.filter((col) => col.columnId),
      includeLineItems: shouldExportLineItems,
      filter: filters,
      ...orderByParam,
      searchTerm: searchPerfromed ? searchTerm : undefined,
      shouldUseFulfillmentPlanAmountSummary: true,
    };

    if (fulfillmentIds && fulfillmentIds.length) {
      requestBody.filter = {
        ...filters,
        collectionFilters: [
          ...(filters.collectionFilters || []),
          {
            column: 'FulfillmentId',
            values: fulfillmentIds,
          },
        ],
      };
    }
    const initiatedByUserId = GlobalSelectors.selectUserCurrentUserId(state);
    const requestUrl = this.formRequestUrlForExports();
    return {
      initiatedByUserId,
      requestUrl,
      requestBody,
    };
  }

  getBatchExportRequestParameters(state, viewRecordsOrSelectedRecords, primaryRecordsOrLineItems, intl, history) {
    const activeFilterView = GlobalSelectors.selectShipmentsFilteringActiveFilterView(state);
    const activeFilterViewId = activeFilterView.filterViewId;
    const appliedFilters = GlobalSelectors.selectBatchDetailsAppliedFilters(state);
    const emptyAction = {};
    const { fulfillmentPlanSetId, statusViewId } = BatchDetailsHelper.getBatchDetailsRequestParams(state, emptyAction, history);
    const filtersAsApiParams = appliedFilters.map(mapCriterionToApiParam);

    const orderByColumn = GlobalSelectors.selectShipmentsGridCurrentOrderByColumn(state, {
      statusViewId,
      activeFilterViewId,
    });
    const orderByParam = ColumnOrderByParamService.getShippingGridOrderByApiParamOrDefault(orderByColumn);
    filtersAsApiParams.push({ orderBy: orderByParam });

    const columnConfiguration = GlobalSelectors.selectShipmentsGridCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
    });
    const exportColumnsWithoutLineItems = this.getRequestColumnConfiguration(columnConfiguration, intl);
    const fulfillmentId =
      viewRecordsOrSelectedRecords === ExportOptions.EXPORT_ALL_RECORDS_IN_VIEW
        ? emptyArray
        : GlobalSelectors.selectSelectedShipmentsSelectedShipmentIds(state) || emptyArray;
    const shouldExportLineItems = primaryRecordsOrLineItems === ExportOptions.EXPORT_PRIMARY_RECORDS_AND_LINE_ITEMS;
    const exportColumns = shouldExportLineItems
      ? [...exportColumnsWithoutLineItems, ...this.getExportColumnsForLineItems(intl)]
      : exportColumnsWithoutLineItems;
    const requestBody = {
      fileType: DEFAULT_EXPORT_FORMAT,
      exportColumns: exportColumns.filter((col) => col.columnId),
      includeLineItems: shouldExportLineItems,
    };

    if (fulfillmentId.length) {
      requestBody.query = {
        fulfillmentId,
      };
    }
    const initiatedByUserId = GlobalSelectors.selectUserCurrentUserId(state);
    const requestUrl = this.formRequestUrlForBatchExports(fulfillmentPlanSetId, filtersAsApiParams);
    return {
      initiatedByUserId,
      requestUrl,
      requestBody,
    };
  }

  formRequestUrlForExports() {
    return '/shipments/defaultExport';
  }

  formRequestUrlForBatchExports(fulfillmentPlanSetId, extraParams) {
    const request = [];
    if (fulfillmentPlanSetId) {
      request.push(`fulfillmentPlanSetId=${fulfillmentPlanSetId}`);
    }
    GeneralExportHelper.addExtraParamsToRequest(extraParams, request);
    return `/fulfillmentPlan/export?${request.join('&').replace(',', '')}`;
  }

  formRequestUrlForCustomExports() {
    return '/seller/customextracts/exportLabelFulfillments';
  }

  getCustomExportRequestParameters(fulfillmentPlanSetId) {
    const requestUrl = this.formRequestUrlForCustomExports(fulfillmentPlanSetId);
    return requestUrl;
  }

  getByFulfillmentIdRequest(extractId, initiatedByUserId, fulfillmentIds) {
    return {
      initiatedByUserId,
      requestBody: {
        extractId,
        fulfillmentIds,
      },
    };
  }

  getCustomExportRequestBody({
    state,
    viewRecordsOrSelectedRecords,
    extractId,
    selectedFulfillmentIds,
    searchTerm,
    fulfillmentPlanSetId,
    filters,
    history,
  }) {
    const exportSelectedRecords = viewRecordsOrSelectedRecords === ExportOptions.EXPORT_SELECTED_RECORDS;
    const initiatedByUserId = GlobalSelectors.selectUserCurrentUserId(state);
    const statusViewId = getSelectedViewFromPathName(history?.location?.pathname)?.id;
    const activeFilterView = GlobalSelectors.selectShipmentsFilteringActiveFilterView(state);
    const orderByColumn = GlobalSelectors.selectShipmentsGridCurrentOrderByColumn(state, {
      statusViewId,
      activeFilterViewId: activeFilterView.filterViewId,
    });
    const orderByParam = ColumnOrderByParamService.getShippingGridOrderByApiParamOrDefault(orderByColumn, true);
    const shouldExportFulfillmentPlansIds = exportSelectedRecords && selectedFulfillmentIds && selectedFulfillmentIds.length;
    const fulfillmentPlanSetFilter = fulfillmentPlanSetId && {
      column: 'FulfillmentPlanSetId',
      values: [fulfillmentPlanSetId],
    };
    const fulfillmentIdFilter = shouldExportFulfillmentPlansIds && {
      column: 'FulfillmentId',
      values: selectedFulfillmentIds,
    };
    const requestBody = {
      extractId,
      filter: filters || {},
      ...orderByParam,
    };
    if (filters?.collectionFilters || fulfillmentIdFilter || fulfillmentPlanSetId) {
      requestBody.filter.collectionFilters = [
        ...(filters?.collectionFilters || []),
        ...(fulfillmentPlanSetFilter ? [fulfillmentPlanSetFilter] : []),
        ...(fulfillmentIdFilter ? [fulfillmentIdFilter] : []),
      ];
    }
    if (searchTerm) {
      requestBody.searchTerm = searchTerm;
    }
    const requestUrl = this.getCustomExportRequestParameters(fulfillmentPlanSetId);
    return {
      requestUrl,
      initiatedByUserId,
      requestBody,
    };
  }
}

export const ShipmentExportHelper = new ShipmentExportHelperClass();
